import { useContext, useState } from 'react'
import cx from 'classnames'

import { type SanityCartSettings } from '@data/sanity/queries/types/cart'
import { CartContext } from '@lib/cart/context'
import { type ErrorMessages } from '@lib/helpers'

import CommentField from './comment-field'
import CartEmpty from './empty'
import CartItem from './item'
import CartSubmit from './submit'
import CartSummary from './summary'
import VatIdField from './vat-id-field'

interface CartFormProps {
  cartSettings: SanityCartSettings
}

const CartForm = ({ cartSettings }: CartFormProps) => {
  const { cart, isCartUpdating, isCartSubmitting } = useContext(CartContext)

  const [vatId, setVatId] = useState('')
  const [comment, setComment] = useState('')
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})

  const handleVatIdChange = (newValue: string) => setVatId(newValue)
  const handleCommentChange = (newValue: string) => setComment(newValue)

  return (
    <div
      className={cx('container pb-8 lg:px-20', {
        'cursor-wait': isCartUpdating || isCartSubmitting,
      })}
    >
      <div className="flex flex-col relative h-full w-full">
        {cart.id && cart.lineItems.length === 0 && (
          <div>
            <div className="flex-1 flex flex-col py-4 sm:py-8 overflow-y-auto">
              <CartEmpty />
            </div>
          </div>
        )}

        {cart.id && cart.lineItems.length > 0 && (
          <div className="relative grid md:grid-cols-2 gap-x-10">
            <div>
              <div className="flex-1 flex flex-col py-4 sm:py-8 overflow-y-auto">
                <div>
                  {cart.lineItems.map((item) => (
                    <CartItem
                      key={item.lineId}
                      item={item}
                      inModal={false}
                      className="mb-10"
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="py-4 sm:py-8">
              <div className="relative pb-4 sm:pb-8">
                {cartSettings.showVatId && (
                  <VatIdField
                    inModal={false}
                    errorMessages={errorMessages}
                    setErrorMessages={setErrorMessages}
                    onChange={handleVatIdChange}
                  />
                )}

                {cartSettings.showComment && (
                  <CommentField
                    onChange={handleCommentChange}
                    inModal={false}
                  />
                )}

                <CartSummary />

                <CartSubmit
                  terms={cartSettings.terms}
                  storeUrl={cartSettings.storeUrl}
                  setErrorMessages={setErrorMessages}
                  vatId={vatId}
                  comment={comment}
                />

                {cartSettings.message && (
                  <p className="mt-4 text-center text-sm sm:text-base">
                    {cartSettings.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CartForm
